import {
  BillGroupStatusEnum,
  IBillGroupRespUI,
  NET_TERMS_DISPLAY,
  NetTermsEnum,
} from '@monetize/types/app';
import { createColumnHelper } from '../../../components/Monetize/DataTableNew/appTable';
import { MText } from '../../../components/Monetize';
import { PastDueIndicatorIcon } from '../../../components/PastDueIndicatorIcon';
import { actionCatalogColumn } from '../../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  checkboxBodyTemplate,
  dateTimeBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getColumnFilterValuesForEnum,
  getCurrencyFilterValues,
  nameWithIdBodyTemplate,
  textBodyTemplate,
} from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import { BILL_GROUP_STATUS_DISPLAY } from '../../../constants/billGroups';
import { getBillGroupPageRoute } from '../../../constants/routes';
import { SETTING_INVOICE_CREATION_DISPLAY } from '../../../constants/settings';

const columnHelper = createColumnHelper<IBillGroupRespUI>();

export interface BillGroupListColumnContext extends ColumnContextBase<IBillGroupRespUI> {
  showPastDue: boolean;
}

/** tableId: local filter state (no FilterStateKeys) */
export const BILL_GROUP_LIST_CATALOG: ColumnCatalog<
  IBillGroupRespUI,
  BillGroupListColumnContext
> = [
  {
    id: '_PAST_DUE_ICON_',
    label: '',
    customizable: false,
    isAvailable: ({ showPastDue }) => showPastDue,
    build: () =>
      columnHelper.display({
        id: '_PAST_DUE_ICON_',
        header: '',
        size: 5,
        cell: (info) => {
          const data = info.row.original;
          return (
            <PastDueIndicatorIcon
              pastDueInvoiceCount={data.pastDueInvoiceCount}
              pastDueAmount={data.pastDueAmountOutstanding}
              minimumInvoiceDueDate={data.minimumInvoiceDueDate}
              currency={data.currency}
            />
          );
        },
      }),
  },
  {
    id: 'name',
    label: 'Name',
    customizable: false,
    build: () =>
      columnHelper.accessor('name', {
        ...getColHeaderWithSize('Name', 300),
        ...getColHeaderFilters('TEXT'),
        cell: nameWithIdBodyTemplate<IBillGroupRespUI, unknown>({
          idProp: 'id',
          nameProp: 'name',
          idLinkFn: (id) => getBillGroupPageRoute(id, 'details'),
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'billingContact',
    label: 'Billing Contact',
    build: () =>
      columnHelper.accessor('billingContact', {
        ...getColHeaderWithSize('Billing Contact', 200),
        cell: (info) => {
          const contact = info.row.original.billingContact;
          return <MText lineClamp={1}>{contact?.fullName ?? ''}</MText>;
        },
        enableSorting: false,
      }),
  },
  {
    id: 'nextInvoiceDate',
    label: 'Next Invoice Date',
    build: () =>
      columnHelper.accessor('nextInvoiceDate', {
        ...getColHeaderWithSize('Next Invoice Date', 200),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'status',
    label: 'Status',
    build: () =>
      columnHelper.accessor('status', {
        ...getColHeaderWithSize('Status', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            BillGroupStatusEnum,
            BILL_GROUP_STATUS_DISPLAY,
          ),
        }),
        cell: enumDisplayBodyTemplate({
          displayMap: BILL_GROUP_STATUS_DISPLAY,
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'currency',
    label: 'Currency',
    build: () =>
      columnHelper.accessor('currency', {
        ...getColHeaderWithSize('Currency'),
        cell: textBodyTemplate(),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getCurrencyFilterValues(),
        }),
        enableSorting: true,
      }),
  },
  {
    id: 'purchaseOrderNumber',
    label: 'PO Number',
    build: () =>
      columnHelper.accessor('purchaseOrderNumber', {
        ...getColHeaderWithSize('PO Number'),
        ...getColHeaderFilters('TEXT'),
        cell: textBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'netTerms',
    label: 'Net Terms',
    build: () =>
      columnHelper.accessor('netTerms', {
        ...getColHeaderWithSize('Net Terms'),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            NetTermsEnum,
            NET_TERMS_DISPLAY,
          ),
        }),
        cell: enumDisplayBodyTemplate({
          displayMap: NET_TERMS_DISPLAY,
        }),
        enableSorting: true,
      }),
  },
  {
    id: 'invoiceCreation',
    label: 'Create Invoices',
    build: () =>
      columnHelper.accessor('invoiceCreation', {
        ...getColHeaderWithSize('Create Invoices'),
        // Not supported on BE
        // ...getColHeaderFilters(
        //   'SET',
        //   getColumnFilterValuesForEnum(
        //     InvoiceCreationEnum,
        //     SETTING_INVOICE_CREATION_DISPLAY,
        //   ),
        // ),
        cell: enumDisplayBodyTemplate({
          displayMap: SETTING_INVOICE_CREATION_DISPLAY,
        }),
        enableSorting: true,
      }),
  },
  {
    id: 'collectInvoiceBalanceAutomatically',
    label: 'Auto-Pay',
    build: () =>
      columnHelper.accessor('collectInvoiceBalanceAutomatically', {
        ...getColHeaderWithSize('Auto-Pay'),
        ...getColHeaderFilters('BOOLEAN'),
        cell: checkboxBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'modifyDate',
    label: 'Updated',
    build: () =>
      columnHelper.accessor('modifyDate', {
        ...getColHeaderWithSize('Updated', 170),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'createDate',
    label: 'Created',
    build: () =>
      columnHelper.accessor('createDate', {
        ...getColHeaderWithSize('Created', 170),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  actionCatalogColumn<IBillGroupRespUI>(),
];
